import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  Box,
  Grid,
  CircularProgress,
  makeStyles,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Save as SaveIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Message as WhatsAppIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from "@material-ui/icons";
import useVPSNotifications from "../hooks/useVPSNotifications";
import useSMTPConfig from "../hooks/useSMTPConfig";
import useEmailTemplates from "../hooks/useEmailTemplates";
import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../../translate/i18n";

const VPS_ALERTS_CHANNEL_ANCHOR_ID = "vps-alerts-canal-notificacion";

/**
 * Validación de canales (email / WhatsApp) según notificationType — misma regla que el guardado en servidor.
 */
const buildChannelValidationMessages = (formData, validateEmailFn, validateWhatsAppNumberFn) => {
  const msgs = [];
  const t = formData.notificationType || "email";

  if (t === "email" || t === "both") {
    if (!formData.smtpConfigId) {
      msgs.push(i18n.t("settings.vpsAlerts.validation.smtpSelect"));
    }
    if (!formData.adminEmail || !String(formData.adminEmail).trim()) {
      msgs.push(i18n.t("settings.vpsAlerts.validation.adminEmailRequired"));
    } else if (!validateEmailFn(formData.adminEmail)) {
      msgs.push(i18n.t("settings.vpsAlerts.validation.adminEmailInvalid"));
    }
  }

  if (t === "whatsapp" || t === "both") {
    if (!formData.whatsappId) {
      msgs.push(i18n.t("settings.vpsAlerts.validation.whatsappSelect"));
    }
    if (!formData.adminWhatsapp || !String(formData.adminWhatsapp).trim()) {
      msgs.push(i18n.t("settings.vpsAlerts.validation.adminWhatsappRequired"));
    } else if (!validateWhatsAppNumberFn(formData.adminWhatsapp)) {
      msgs.push(i18n.t("settings.vpsAlerts.validation.adminWhatsappInvalid"));
    }
    if (!formData.whatsappMessageTemplate || !String(formData.whatsappMessageTemplate).trim()) {
      msgs.push(i18n.t("settings.vpsAlerts.validation.whatsappTemplateRequired"));
    }
  }

  return msgs;
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh"
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  infoBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' 
      ? 'rgba(33, 150, 243, 0.1)' 
      : 'rgba(33, 150, 243, 0.05)',
    borderRadius: 8,
    border: `1px solid ${theme.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'}`
  },
  warningBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' 
      ? 'rgba(255, 152, 0, 0.1)' 
      : 'rgba(255, 152, 0, 0.05)',
    borderRadius: 8,
    border: `1px solid ${theme.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'}`
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    marginTop: theme.spacing(3)
  },
  testButton: {
    marginTop: theme.spacing(1)
  },
  testModal: {
    "& .MuiDialog-paper": {
      backgroundColor: theme.palette.background.paper
    }
  },
  testResult: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderRadius: 8
  }
}));

const NotificationAlertsConfig = () => {
  const classes = useStyles();
  const {
    loading,
    saving,
    error: loadError,
    config,
    sendingTest,
    saveConfig,
    sendTestNotification,
    fetchConfig
  } = useVPSNotifications();

  // Hooks para obtener datos de SMTP, plantillas y WhatsApp
  const { configs: smtpConfigs } = useSMTPConfig();
  const { templates: emailTemplates } = useEmailTemplates();
  const { whatsApps } = useContext(WhatsAppsContext);

  // Estados del formulario
  const [formData, setFormData] = useState({
    enabled: false,
    notificationType: 'email',
    emailEnabled: false,
    smtpConfigId: null,
    emailTemplateId: null,
    adminEmail: '',
    whatsappEnabled: false,
    whatsappId: null,
    whatsappMessageTemplate: i18n.t("settings.vpsAlerts.defaultWhatsAppTemplate"),
    adminWhatsapp: '',
    enableDiskAlerts: true,
    enableBinaryLogAlerts: true,
    enableCpuAlerts: true,
    enableMemoryAlerts: true,
    enableCriticalAlerts: true,
    diskAlertThreshold: 75,
    binaryLogAlertThreshold: 50.00,
    cpuAlertThreshold: 80,
    memoryAlertThreshold: 85,
    enableAutoCleanup: false,
    autoCleanupDiskThreshold: 75,
    autoCleanupBinaryLogThresholdGB: 40,
    alertFrequency: 6
  });

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState({ title: '', message: '', type: 'info' }); // 'info' | 'success' | 'error' | 'warning'

  // Función helper para convertir valores a boolean
  const toBoolean = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === 'true' || value === '1';
    return Boolean(value);
  };

  // Cargar configuración cuando esté disponible
  useEffect(() => {
    if (config) {
      // Buscar plantilla unificada VPS por defecto
      const defaultVpsTemplate = emailTemplates.find(
        t => t.purpose === 'vps_alert' && t.name && t.name.includes('Completa')
      );
      
      // Plantilla WhatsApp por defecto
      const defaultWhatsAppTemplate = i18n.t("settings.vpsAlerts.defaultWhatsAppTemplate");
      
      // Si no hay plantilla configurada, usar la unificada por defecto
      const emailTemplateId = config.emailTemplateId || (defaultVpsTemplate ? defaultVpsTemplate.id : null);
      
      // Si no hay plantilla WhatsApp configurada, usar la por defecto
      const whatsappTemplate = config.whatsappMessageTemplate || defaultWhatsAppTemplate;

      setFormData(prev => ({
        ...prev,
        enabled: toBoolean(config.enabled),
        notificationType: config.notificationType || 'email',
        emailEnabled: toBoolean(config.emailEnabled),
        smtpConfigId: config.smtpConfigId || null,
        emailTemplateId: emailTemplateId,
        adminEmail: config.adminEmail || '',
        whatsappEnabled: toBoolean(config.whatsappEnabled),
        whatsappId: config.whatsappId || null,
        whatsappMessageTemplate: whatsappTemplate,
        adminWhatsapp: config.adminWhatsapp || '',
        enableDiskAlerts: config.enableDiskAlerts !== undefined ? toBoolean(config.enableDiskAlerts) : true,
        enableBinaryLogAlerts: config.enableBinaryLogAlerts !== undefined ? toBoolean(config.enableBinaryLogAlerts) : true,
        enableCpuAlerts: config.enableCpuAlerts !== undefined ? toBoolean(config.enableCpuAlerts) : true,
        enableMemoryAlerts: config.enableMemoryAlerts !== undefined ? toBoolean(config.enableMemoryAlerts) : true,
        enableCriticalAlerts: config.enableCriticalAlerts !== undefined ? toBoolean(config.enableCriticalAlerts) : true,
        diskAlertThreshold: config.diskAlertThreshold || 75,
        binaryLogAlertThreshold: config.binaryLogAlertThreshold || 50.00,
        cpuAlertThreshold: config.cpuAlertThreshold || 80,
        memoryAlertThreshold: config.memoryAlertThreshold || 85,
        enableAutoCleanup: toBoolean(config.enableAutoCleanup),
        autoCleanupDiskThreshold: config.autoCleanupDiskThreshold || 75,
        autoCleanupBinaryLogThresholdGB:
          config.autoCleanupBinaryLogThresholdGB !== undefined && config.autoCleanupBinaryLogThresholdGB !== null
            ? Number(config.autoCleanupBinaryLogThresholdGB)
            : 40,
        alertFrequency: config.alertFrequency || 6
      }));
    }
  }, [config, emailTemplates]);

  /**
   * Manejar cambios en campos del formulario
   */
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Manejar cambio de tipo de notificación
   */
  const handleNotificationTypeChange = (e) => {
    const type = e.target.value;
    setFormData(prev => ({
      ...prev,
      notificationType: type,
      emailEnabled: type === 'email' || type === 'both',
      whatsappEnabled: type === 'whatsapp' || type === 'both'
    }));
  };

  /**
   * Mostrar modal informativo
   */
  const showInfoModal = (title, message, type = 'info') => {
    setInfoModalContent({ title, message, type });
    setInfoModalOpen(true);
  };

  const activeSmtpConfigs = smtpConfigs.filter(c => c.isActive);
  const connectedWhatsApps = whatsApps.filter(w => w.status === 'CONNECTED');
  const dateLocale =
    i18n.language === "pt" ? "pt-BR" : i18n.language === "en" ? "en-US" : "es-ES";

  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateWhatsAppNumber = (number) => {
    if (!number) return true;
    return /^[+]?[0-9]{10,15}$/.test(number);
  };

  const scrollToNotificationChannelSection = () => {
    requestAnimationFrame(() => {
      const el = document.getElementById(VPS_ALERTS_CHANNEL_ANCHOR_ID);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const showValidationIssuesModal = (title, issues, extraNote = "") => {
    const body =
      `${i18n.t("settings.vpsAlerts.validationModal.bodyIntro")}\n\n• ` +
      issues.join("\n• ") +
      (extraNote ? `\n\n${extraNote}` : "");
    showInfoModal(title, body, "warning");
    scrollToNotificationChannelSection();
  };

  /**
   * Enviar notificación de prueba
   * Permite enviar prueba incluso si el sistema no está habilitado completamente
   */
  const handleSendTestNotification = async () => {
    const channelIssues = buildChannelValidationMessages(formData, validateEmail, validateWhatsAppNumber);
    if (channelIssues.length > 0) {
      let extra = "";
      const t = formData.notificationType || "email";
      if ((t === "email" || t === "both") && activeSmtpConfigs.length === 0) {
        extra = i18n.t("settings.vpsAlerts.notes.noActiveSmtpTest");
      }
      if ((t === "whatsapp" || t === "both") && connectedWhatsApps.length === 0) {
        extra = extra
          ? `${extra}\n\n${i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp")}`
          : i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp");
      }
      showValidationIssuesModal(
        i18n.t("settings.vpsAlerts.validationModal.titleTest"),
        channelIssues,
        extra
      );
      return;
    }

    // Abrir modal de carga
    setTestModalOpen(true);
    setTestResult(null);

    try {
      // Enviar datos del formulario para permitir pruebas sin guardar
      const result = await sendTestNotification({
        smtpConfigId: formData.smtpConfigId,
        adminEmail: formData.adminEmail,
        emailTemplateId: formData.emailTemplateId,
        whatsappId: formData.whatsappId,
        adminWhatsapp: formData.adminWhatsapp,
        whatsappMessageTemplate: formData.whatsappMessageTemplate,
        notificationType: formData.notificationType
      });
      
      if (result.success) {
        const sentMessages = [];
        if (result.sent?.email) sentMessages.push(i18n.t("settings.vpsAlerts.test.lineEmailOk"));
        if (result.sent?.whatsapp) sentMessages.push(i18n.t("settings.vpsAlerts.test.lineWhatsappOk"));
        let footer = i18n.t("settings.vpsAlerts.test.footerEmail");
        if (result.sent?.email && result.sent?.whatsapp) {
          footer = i18n.t("settings.vpsAlerts.test.footerBoth");
        } else if (result.sent?.whatsapp) {
          footer = i18n.t("settings.vpsAlerts.test.footerWhatsapp");
        }
        setTestResult({
          success: true,
          message: `${i18n.t("settings.vpsAlerts.test.successIntro")}\n\n${sentMessages.join("\n")}\n\n${footer}`
        });
      } else {
        setTestResult({
          success: false,
          message: `${i18n.t("settings.vpsAlerts.test.errorIntro")}\n\n${result.errors?.join("\n") || result.message || i18n.t("settings.vpsAlerts.test.unknownError")}`
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: `${i18n.t("settings.vpsAlerts.test.errorIntro")}\n\n${error.message || i18n.t("settings.vpsAlerts.test.unknownError")}`
      });
    }
  };

  /**
   * Guardar configuración
   */
  const handleSave = async () => {
    if (formData.enabled) {
      const channelIssues = buildChannelValidationMessages(formData, validateEmail, validateWhatsAppNumber);
      if (channelIssues.length > 0) {
        let extra = "";
        const t = formData.notificationType || "email";
        if ((t === "email" || t === "both") && activeSmtpConfigs.length === 0) {
          extra = i18n.t("settings.vpsAlerts.notes.noActiveSmtpSave");
        }
        if ((t === "whatsapp" || t === "both") && connectedWhatsApps.length === 0) {
          extra = extra
            ? `${extra}\n\n${i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp")}`
            : i18n.t("settings.vpsAlerts.notes.noConnectedWhatsapp");
        }
        showValidationIssuesModal(
          i18n.t("settings.vpsAlerts.validationModal.titleSave"),
          channelIssues,
          extra
        );
        return;
      }
    }

    try {
      const result = await saveConfig(formData);
      // saveConfig no lanza en error: devuelve null y suele mostrar toast.error
      if (!result) {
        showInfoModal(
          i18n.t("settings.vpsAlerts.save.notSavedTitle"),
          i18n.t("settings.vpsAlerts.save.notSavedBody"),
          "warning"
        );
        return;
      }
      showInfoModal(
        i18n.t("settings.vpsAlerts.save.successTitle"),
        i18n.t("settings.vpsAlerts.save.successBody"),
        "success"
      );
    } catch (error) {
      console.error("Error guardando configuración:", error);
      showInfoModal(
        i18n.t("settings.vpsAlerts.save.errorTitle"),
        i18n.t("settings.vpsAlerts.save.errorBody", {
          message: error.message || i18n.t("settings.vpsAlerts.test.unknownError")
        }),
        "error"
      );
    }
  };

  if (loading && !config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && loadError && !config) {
    return (
      <div className={classes.container}>
        <Card className={classes.card}>
          <CardContent>
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {i18n.t("settings.vpsAlerts.loadError", { error: loadError })}
            </Alert>
            <Button variant="outlined" color="primary" onClick={() => fetchConfig()}>
              {i18n.t("settings.vpsAlerts.retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Sección 1: Habilitación General */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("settings.vpsAlerts.generalSection")}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={handleChange('enabled')}
                color="primary"
              />
            }
            label={i18n.t("settings.vpsAlerts.toggleLabel")}
          />

          {/* Info Box */}
          <Box className={classes.infoBox} mt={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <InfoIcon style={{ marginRight: 8, color: '#2196F3' }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {i18n.t("settings.vpsAlerts.infoWhyTitle")}
              </Typography>
            </Box>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {i18n.t("settings.vpsAlerts.infoIntro")}
            </Typography>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>{i18n.t("settings.vpsAlerts.infoLi1")}</li>
              <li>{i18n.t("settings.vpsAlerts.infoLi2")}</li>
              <li>{i18n.t("settings.vpsAlerts.infoLi3")}</li>
              <li>{i18n.t("settings.vpsAlerts.infoLi4")}</li>
            </ul>
            <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic' }}>
              {i18n.t("settings.vpsAlerts.infoClosing")}
            </Typography>
          </Box>

          {/* Warning Box */}
          {formData.enabled && (
            <Box className={classes.warningBox} mt={2}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                <span role="img" aria-label="warning">⚠️</span> {i18n.t("settings.vpsAlerts.warningEnabled")}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Sección 2: Configuración de Notificaciones (solo si enabled) */}
      {formData.enabled && (
        <Card id={VPS_ALERTS_CHANNEL_ANCHOR_ID} className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("settings.vpsAlerts.notificationsSection")}
            </Typography>

            <FormControl component="fieldset" className={classes.formField}>
              <RadioGroup
                row
                value={formData.notificationType}
                onChange={handleNotificationTypeChange}
              >
                <FormControlLabel value="email" control={<Radio />} label={i18n.t("settings.vpsAlerts.channelEmail")} />
                <FormControlLabel value="whatsapp" control={<Radio />} label={i18n.t("settings.vpsAlerts.channelWhatsapp")} />
                <FormControlLabel value="both" control={<Radio />} label={i18n.t("settings.vpsAlerts.channelBoth")} />
              </RadioGroup>
            </FormControl>

            <Divider style={{ margin: '16px 0' }} />

            {/* Formulario Email */}
            {(formData.notificationType === 'email' || formData.notificationType === 'both') && (
              <Box mb={3}>
                <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                  <EmailIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {i18n.t("settings.vpsAlerts.emailSection")}
                </Typography>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>{i18n.t("settings.vpsAlerts.smtpRequiredLabel")}</InputLabel>
                  <Select
                    value={formData.smtpConfigId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, smtpConfigId: e.target.value || null }))}
                    label={i18n.t("settings.vpsAlerts.smtpRequiredLabel")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("settings.vpsAlerts.selectSmtp")}</em>
                    </MenuItem>
                    {activeSmtpConfigs.map((smtp) => (
                      <MenuItem key={smtp.id} value={smtp.id}>
                        {smtp.name}{smtp.isDefault ? ` (${i18n.t("settings.smtpManager.defaultChip")})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>{i18n.t("settings.vpsAlerts.emailTemplateOptional")}</InputLabel>
                  <Select
                    value={formData.emailTemplateId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, emailTemplateId: e.target.value || null }))}
                    label={i18n.t("settings.vpsAlerts.emailTemplateOptional")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("settings.vpsAlerts.useDefaultTemplates")}</em>
                    </MenuItem>
                    {emailTemplates
                      .filter(template => 
                        template.purpose && 
                        (template.purpose === 'vps_alert' || template.purpose.startsWith('vps_alert'))
                      )
                      .map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name} - {template.purpose}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label={i18n.t("settings.vpsAlerts.adminEmailLabel")}
                  value={formData.adminEmail}
                  onChange={handleChange('adminEmail')}
                  className={classes.formField}
                  error={!!(formData.adminEmail && !validateEmail(formData.adminEmail))}
                  helperText={
                    formData.adminEmail && !validateEmail(formData.adminEmail)
                      ? i18n.t("settings.vpsAlerts.invalidEmail")
                      : i18n.t("settings.vpsAlerts.adminEmailHint")
                  }
                />
              </Box>
            )}

            {/* Formulario WhatsApp */}
            {(formData.notificationType === 'whatsapp' || formData.notificationType === 'both') && (
              <Box>
                <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                  <WhatsAppIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {i18n.t("settings.vpsAlerts.whatsappSection")}
                </Typography>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>{i18n.t("settings.vpsAlerts.whatsappConnectionLabel")}</InputLabel>
                  <Select
                    value={formData.whatsappId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, whatsappId: e.target.value || null }))}
                    label={i18n.t("settings.vpsAlerts.whatsappConnectionLabel")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("settings.vpsAlerts.selectWhatsapp")}</em>
                    </MenuItem>
                    {connectedWhatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name} ({whatsapp.number})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label={i18n.t("settings.vpsAlerts.whatsappTemplateLabel")}
                  value={formData.whatsappMessageTemplate}
                  onChange={handleChange('whatsappMessageTemplate')}
                  className={classes.formField}
                  helperText={i18n.t("settings.vpsAlerts.whatsappTemplateVars")}
                />

                <TextField
                  fullWidth
                  label={i18n.t("settings.vpsAlerts.adminWhatsappLabel")}
                  value={formData.adminWhatsapp}
                  onChange={handleChange('adminWhatsapp')}
                  className={classes.formField}
                  placeholder="+1234567890"
                  error={!!(formData.adminWhatsapp && !validateWhatsAppNumber(formData.adminWhatsapp))}
                  helperText={
                    formData.adminWhatsapp && !validateWhatsAppNumber(formData.adminWhatsapp)
                      ? i18n.t("settings.vpsAlerts.invalidWhatsapp")
                      : i18n.t("settings.vpsAlerts.adminWhatsappHint")
                  }
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sección 3: Umbrales de Alertas (solo si enabled) */}
      {formData.enabled && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("settings.vpsAlerts.thresholdsSection")}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.diskPct")}
                  value={formData.diskAlertThreshold}
                  onChange={handleChange('diskAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText={i18n.t("settings.vpsAlerts.diskHelp")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.binaryGb")}
                  value={formData.binaryLogAlertThreshold}
                  onChange={handleChange('binaryLogAlertThreshold')}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={i18n.t("settings.vpsAlerts.binaryHelp")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.cpuPct")}
                  value={formData.cpuAlertThreshold}
                  onChange={handleChange('cpuAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText={i18n.t("settings.vpsAlerts.cpuHelp")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.memPct")}
                  value={formData.memoryAlertThreshold}
                  onChange={handleChange('memoryAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText={i18n.t("settings.vpsAlerts.memHelp")}
                />
              </Grid>
            </Grid>

            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableDiskAlerts}
                    onChange={handleChange('enableDiskAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableDisk")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableBinaryLogAlerts}
                    onChange={handleChange('enableBinaryLogAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableBinary")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCpuAlerts}
                    onChange={handleChange('enableCpuAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableCpu")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableMemoryAlerts}
                    onChange={handleChange('enableMemoryAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableMem")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCriticalAlerts}
                    onChange={handleChange('enableCriticalAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableCritical")}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sección 4: Frecuencia de Notificaciones y Limpieza Automática (solo si enabled) */}
      {formData.enabled && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("settings.vpsAlerts.frequencySection")}
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.alertFreqLabel")}
                  value={formData.alertFrequency}
                  onChange={handleChange('alertFrequency')}
                  inputProps={{ min: 1, max: 48 }}
                  helperText={i18n.t("settings.vpsAlerts.alertFreqHelp")}
                />
              </Grid>
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
              {i18n.t("settings.vpsAlerts.cleanupSubtitle")}
            </Typography>

            <Box className={classes.infoBox} mb={2}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                {i18n.t("settings.vpsAlerts.cleanupInfoTitle")}
              </Typography>
              <Typography variant="body2">
                {i18n.t("settings.vpsAlerts.cleanupInfoIntro")}
              </Typography>
              <ul style={{ marginTop: 8, paddingLeft: 20, marginBottom: 0 }}>
                <li>{i18n.t("settings.vpsAlerts.cleanupInfoLi1")}</li>
                <li>{i18n.t("settings.vpsAlerts.cleanupInfoLi2")}</li>
              </ul>
              <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic', color: '#c62828' }}>
                {i18n.t("settings.vpsAlerts.cleanupRisk")}
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enableAutoCleanup}
                  onChange={handleChange('enableAutoCleanup')}
                  color="primary"
                />
              }
              label={i18n.t("settings.vpsAlerts.enableAutoCleanup")}
            />

            {formData.enableAutoCleanup && (
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                    {i18n.t("settings.vpsAlerts.cleanupCooldownNote", { hours: formData.alertFrequency })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={i18n.t("settings.vpsAlerts.cleanupDiskThreshold")}
                    value={formData.autoCleanupDiskThreshold}
                    onChange={handleChange('autoCleanupDiskThreshold')}
                    inputProps={{ min: 0, max: 100 }}
                    helperText={i18n.t("settings.vpsAlerts.cleanupDiskHelp")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={i18n.t("settings.vpsAlerts.cleanupBinlogGb")}
                    value={formData.autoCleanupBinaryLogThresholdGB}
                    onChange={handleChange('autoCleanupBinaryLogThresholdGB')}
                    inputProps={{ min: 0, step: 0.5 }}
                    helperText={i18n.t("settings.vpsAlerts.cleanupBinlogHelp")}
                  />
                </Grid>
                {config && config.lastBinaryLogAutoCleanupAt && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("settings.vpsAlerts.lastCleanup", {
                        date: new Date(config.lastBinaryLogAutoCleanupAt).toLocaleString(dateLocale, {
                          dateStyle: 'short',
                          timeStyle: 'short'
                        })
                      })}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Botones de Acción - Solo visibles cuando el sistema está habilitado */}
            <Box className={classes.actions} style={{ marginTop: 24 }}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={() => handleSave()}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? i18n.t("settings.vpsAlerts.saving") : i18n.t("settings.vpsAlerts.saveConfig")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSendTestNotification}
                disabled={sendingTest || (!formData.smtpConfigId && !formData.whatsappId)}
                startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {sendingTest ? i18n.t("settings.vpsAlerts.sending") : i18n.t("settings.vpsAlerts.sendTest")}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Modal de Resultado de Prueba de Notificación */}
      <Dialog
        open={testModalOpen}
        onClose={() => {}} // No permitir cerrar con click fuera o ESC durante la prueba
        className={classes.testModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {!testResult ? (
              <>
                <CircularProgress size={24} style={{ marginRight: 12 }} />
                <span>{i18n.t("settings.vpsAlerts.testModal.sending")}</span>
              </>
            ) : (
              <>
                {testResult.success ? (
                  <CheckCircleIcon style={{ color: '#4caf50', marginRight: 12 }} />
                ) : (
                  <CancelIcon style={{ color: '#f44336', marginRight: 12 }} />
                )}
                <span>
                  {testResult.success ? i18n.t("settings.vpsAlerts.testModal.success") : i18n.t("settings.vpsAlerts.testModal.error")}
                </span>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {testResult ? (
            <Box>
              <Alert
                severity={testResult.success ? "success" : "error"}
                className={classes.testResult}
                icon={testResult.success ? <CheckCircleIcon /> : <CancelIcon />}
              >
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {testResult.message}
                </Typography>
              </Alert>
              {testResult.success && (
                <Box mt={2} p={2} style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 8 }}>
                  <Typography variant="body2" style={{ color: '#4caf50' }}>
                    <strong><span role="img" aria-label="lightbulb">💡</span> {i18n.t("settings.vpsAlerts.testModal.tipPrefix")}</strong>{' '}
                    {formData.notificationType === 'both'
                      ? i18n.t("settings.vpsAlerts.testModal.tipBoth")
                      : formData.notificationType === 'whatsapp'
                      ? i18n.t("settings.vpsAlerts.testModal.tipWhatsapp")
                      : i18n.t("settings.vpsAlerts.testModal.tipEmail")}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" py={3}>
              <CircularProgress size={48} />
              <Typography variant="body1" style={{ marginTop: 16 }}>
                {i18n.t("settings.vpsAlerts.testModal.pleaseWait")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setTestModalOpen(false)} 
            color="primary"
            variant="contained"
            disabled={!testResult} // Solo permitir cerrar cuando haya resultado
          >
            {testResult ? i18n.t("settings.vpsAlerts.testModal.close") : i18n.t("settings.vpsAlerts.testModal.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Informativo General */}
      <Dialog
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        className={classes.testModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {infoModalContent.type === 'success' && <CheckCircleIcon style={{ color: '#4caf50', marginRight: 12 }} />}
            {infoModalContent.type === 'error' && <CancelIcon style={{ color: '#f44336', marginRight: 12 }} />}
            {infoModalContent.type === 'warning' && <InfoIcon style={{ color: '#ff9800', marginRight: 12 }} />}
            {infoModalContent.type === 'info' && <InfoIcon style={{ color: '#2196f3', marginRight: 12 }} />}
            <span>{infoModalContent.title}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
            {infoModalContent.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setInfoModalOpen(false)} 
            color="primary"
            variant="contained"
          >
            {i18n.t("settings.vpsAlerts.accept")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NotificationAlertsConfig;
